import useWebSocket from "react-use-websocket";
import {
    InputAudioBufferAppendCommand,
    InputAudioBufferClearCommand,
    Message,
    ResponseAudioDelta,
    ResponseAudioTranscriptDelta,
    ResponseDone,
    SessionUpdateCommand,
    ExtensionMiddleTierToolResponse,
    ResponseInputAudioTranscriptionCompleted,
    ResponseAudioTranscriptDone,
    ResponseFunctionCallArgumentsDone
} from "@/types";

import { getToolDefinitions, executeTool } from "@/hooks/utils/tools";

type Parameters = {
    candidateId?: string;
    useDirectAoaiApi?: boolean;
    aoaiEndpointOverride?: string;
    aoaiApiKeyOverride?: string;
    aoaiModelOverride?: string;

    enableInputAudioTranscription?: boolean;
    onWebSocketOpen?: () => void;
    onWebSocketClose?: () => void;
    onWebSocketError?: (event: Event) => void;
    onWebSocketMessage?: (event: MessageEvent<any>) => void;

    onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
    onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
    onReceivedResponseAudioTranscriptDone?: (message: ResponseAudioTranscriptDone) => void;
    onReceivedResponseDone?: (message: ResponseDone) => void;
    onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
    onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
    onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
    onReceivedError?: (message: Message) => void;
};

export default function useRealTime({
    useDirectAoaiApi,
    aoaiEndpointOverride,
    aoaiApiKeyOverride,
    aoaiModelOverride,
    enableInputAudioTranscription,
    onWebSocketOpen,
    onWebSocketClose,
    onWebSocketError,
    onWebSocketMessage,
    onReceivedResponseDone,
    onReceivedResponseAudioDelta,
    onReceivedResponseAudioTranscriptDelta,
    onReceivedInputAudioBufferSpeechStarted,
    onReceivedExtensionMiddleTierToolResponse,
    onReceivedInputAudioTranscriptionCompleted,
    onReceivedError,
    onReceivedResponseAudioTranscriptDone
}: Parameters) {
    // Build the WebSocket endpoint
    const wsEndpoint = useDirectAoaiApi
        ? `${aoaiEndpointOverride}/openai/realtime?api-key=${aoaiApiKeyOverride}&deployment=${aoaiModelOverride}&api-version=2024-10-01-preview`
        : `/realtime`;

    // Initialize the WebSocket connection
    const { sendJsonMessage, getWebSocket } = useWebSocket(wsEndpoint, {
        onOpen: () => onWebSocketOpen?.(),
        onClose: () => onWebSocketClose?.(),
        onError: event => onWebSocketError?.(event),
        onMessage: event => onMessageReceived(event),
        shouldReconnect: () => true
    });

    // Start a session and send tool definitions to the server
    const startSession = () => {
        const command: SessionUpdateCommand = {
            type: "session.update",
            session: {
                turn_detection: {
                    type: "server_vad",
                    threshold: 0.67,
                    prefix_padding_ms: 400,
                    silence_duration_ms: 700
                },
                tools: getToolDefinitions(),
                tool_choice: "auto"
            }
        };

        if (enableInputAudioTranscription) {
            command.session.input_audio_transcription = {
                model: "whisper-1"
            };
        }
        sendJsonMessage(command);
        // sendTextMessage();
    };

    // const sendTextMessage = () => {
    //     const command = {
    //         type: "response.create",
    //         response: {}
    //     };
    //     sendJsonMessage(command);
    // };

    const addUserAudio = (base64Audio: string) => {
        const command: InputAudioBufferAppendCommand = {
            type: "input_audio_buffer.append",
            audio: base64Audio
        };
        sendJsonMessage(command);
    };

    const inputAudioBufferClear = () => {
        const command: InputAudioBufferClearCommand = {
            type: "input_audio_buffer.clear"
        };
        sendJsonMessage(command);
    };

    // Handle function call messages from the server
    const handleFunctionCall = async (message: ResponseFunctionCallArgumentsDone) => {
        console.log("[Function Call] Received function call request:", message);
        try {
            const functionName = message.call_id;
            const functionArgs = JSON.parse(message.arguments);
            console.log(`[Function Call] Executing function: ${functionName} with args:`, functionArgs);
            const result = await executeTool(functionName, functionArgs);
            console.log("[Function Call] Execution result:", result);
        } catch (error) {
            console.error("[Function Call] Error executing function:", error);
        }
    };

    // Main WebSocket message handler
    const onMessageReceived = (event: MessageEvent<any>) => {
        onWebSocketMessage?.(event);
        let message: Message;
        try {
            message = JSON.parse(event.data);
        } catch (e) {
            console.error("Failed to parse JSON message:", e);
            throw e;
        }
        switch (message.type) {
            case "response.done":
                onReceivedResponseDone?.(message as ResponseDone);
                break;
            case "response.audio.delta":
                onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
                break;
            case "response.audio_transcript.delta":
                onReceivedResponseAudioTranscriptDelta?.(message as ResponseAudioTranscriptDelta);
                break;
            case "input_audio_buffer.speech_started":
                onReceivedInputAudioBufferSpeechStarted?.(message);
                break;
            case "conversation.item.input_audio_transcription.completed":
                onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
                break;
            case "extension.middle_tier_tool_response":
                onReceivedExtensionMiddleTierToolResponse?.(message as ExtensionMiddleTierToolResponse);
                break;
            case "response.audio_transcript.done":
                onReceivedResponseAudioTranscriptDone?.(message as ResponseAudioTranscriptDone);
                break;
            case "response.function_call_arguments.done":
                handleFunctionCall(message as ResponseFunctionCallArgumentsDone);
                break;
            case "error":
                onReceivedError?.(message);
                break;
            default:
                break;
        }
    };

    // New method: closeSession.
    // This sends a command to the backend to close the session and then closes the WebSocket.
    const closeSession = () => {
        console.log("[WebSocket] Sending session close command");
        const command = {
            type: "session.close"
        };
        sendJsonMessage(command);

        // If you need to force-close the WebSocket connection, access it via getWebSocket
        const ws = getWebSocket();
        if (ws && ws.readyState === WebSocket.OPEN) {
            console.log("[WebSocket] Closing WebSocket connection");
            ws.close();
        } else {
            console.log("[WebSocket] WebSocket is not open, no need to close");
        }
    };

    return { startSession, addUserAudio, inputAudioBufferClear, closeSession };
}
